import { useState, useEffect, useCallback } from "react";
import CircularProgressBar from "../../../CommonComponents/CircularProgressBar";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import Loading from "../../../Notification-Loading/Loading/Loading";
import { useInfiniteScroll } from "../../../UserReport/UserActivityReport/useInfiniteScroll";
import { useDebouncedValue } from "../../../../Hooks/useDebounceValue";
import { useRef } from "react";


/* ================= Icons ================= */
const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="#027A48" strokeWidth="2" />
    <path d="M8 12l3 3 5-5" stroke="#027A48" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TargetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="#175CD3" strokeWidth="2" />
    <circle cx="12" cy="12" r="6" stroke="#175CD3" strokeWidth="2" />
    <circle cx="12" cy="12" r="2" fill="#175CD3" />
  </svg>
);

const TrendUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M3 17l5-5 4 4 9-9" stroke="#12B76A" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M14 7h6v6" stroke="#12B76A" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const CodeIcon = ({ title }) => {
  return (
    title === "Mock Tests" ? (<svg
      width="20"
      height="20"
      fill="none"
      viewBox="0 0 28 28"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeWidth="2"
        stroke="#F96343"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M24.5 20.9999L23.3332 22.2763C22.7144 22.9531 21.8752 23.3333 21.0001 23.3333C20.1251 23.3333 19.2859 22.9531 18.6671 22.2763C18.0474 21.6009 17.2083 21.2217 16.3335 21.2217C15.4588 21.2217 14.6197 21.6009 14 22.2763M3.5 23.3333H5.45363C6.02435 23.3333 6.3097 23.3333 6.57824 23.2688C6.81632 23.2116 7.04393 23.1174 7.2527 22.9894C7.48817 22.8451 7.68995 22.6434 8.0935 22.2398L22.75 7.58327C23.7165 6.61677 23.7165 5.04977 22.75 4.08327C21.7835 3.11677 20.2165 3.11677 19.25 4.08327L4.59347 18.7398C4.18992 19.1434 3.98814 19.3451 3.84384 19.5806C3.71591 19.7894 3.62163 20.017 3.56447 20.2551C3.5 20.5236 3.5 20.809 3.5 21.3797V23.3333Z"
      />
    </svg>) : (<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M8 6l-6 6 6 6M16 6l6 6-6 6" stroke="#F96343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>)
  )
};

/* ================= Helper ================= */
const toNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const LIMIT = 6;

const formatBatch = (name, detailsArray) => {
  const details = detailsArray?.[0]?.assignmentDetails ?? {};
  return {
    name,
    totalStudents: toNumber(details.totalLearners),
    assignmentCompletion: {
      percentage: toNumber(details.overallAssignmentCompletionPercentage),
      growth: toNumber(details.assignmentGrowthPercentage),
    },
    averageScore: {
      value: toNumber(details.averageAssignmentScore),
      growth: toNumber(details.assigmentScoreGrowthPercentage),
    },
    codingChallenges: {
      activeUsers: toNumber(details.ccActiveUsers),
      percentage: toNumber(details.ccCompletedPercentage),
      avgProblemsSolved: toNumber(details.ccAvgSolvedByLearner),
    },
    mockTests: {
      activeUsers: toNumber(details.mocktestActiveUsers),
      percentage: toNumber(details.mockCompletedPercentage),
      avgTestsPerStudent: toNumber(details.avgMockSolvedByLearner),
    },
  };
};

/* ================= Sub-components ================= */
const StatCard = ({ title, value, change, type, icon }) => (
  <div className="stat-card">
    <div className={`stat-card-header ${type}`}>
      {icon}
      <span>{title}</span>
    </div>
    <div className="stat-card-body">
      <div className="stat-value-wrapper">
        <TrendUpIcon />
        <span className="stat-value">{value}</span>
      </div>
      <p className="stat-change">{change}</p>
    </div>
  </div>
);

const ChallengeCard = ({ title, activeUsers, percent, avgLabel, avgValue }) => (
  <div className="challenge-card">
    <div className="challenge-header">
      <CodeIcon title={title} />
      <span>{title}</span>
    </div>
    <div className="challenge-body">
      <div className="box">
        <p>Active Users</p>
        <div className="progress-wrapper">
          <div className="circular-progress-container">
            <CircularProgressBar
              percentage={percent}
              color="#F96343"
              circleRatio={1}
              text={`${percent}%`}
            />
          </div>
          <span className="active-users-count">{activeUsers}</span>
        </div>
      </div>
      <div className="box">
        <p>{avgLabel}</p>
        <h2>{avgValue}</h2>
      </div>
    </div>
  </div>
);

const BatchSection = ({ data, innerRef }) => (
  <div className="batch-section" ref={innerRef}>
    <div className="batch-header">
      <h3>{data.name}</h3>
      <span className="students-count">{data.totalStudents} Students</span>
    </div>
    <div className="batch-body">
      <div className="stats">
        <StatCard
          title="Assignment Completion"
          value={`${data.assignmentCompletion.percentage}%`}
          change={`${data.assignmentCompletion.growth >= 0 ? "+" : ""}${data.assignmentCompletion.growth}% from last week`}
          type="success"
          icon={<CheckIcon />}
        />
        <StatCard
          title="Average Score"
          value={`${data.averageScore.value}`}
          change={`${data.averageScore.growth >= 0 ? "+" : ""}${data.averageScore.growth}% from last week`}
          type="blue"
          icon={<TargetIcon />}
        />
      </div>
      <div className="challenges">
        <ChallengeCard
          title="Coding Challenges"
          activeUsers={data.codingChallenges.activeUsers}
          percent={data.codingChallenges.percentage}
          avgLabel="Avg Problems Solved"
          avgValue={data.codingChallenges.avgProblemsSolved}
        />
        <ChallengeCard
          title="Mock Tests"
          activeUsers={data.mockTests.activeUsers}
          percent={data.mockTests.percentage}
          avgLabel="Avg Tests/Student"
          avgValue={data.mockTests.avgTestsPerStudent}
        />
      </div>
    </div>
  </div>
);

/* ================= Main ================= */
export default function BatchOverview({ search = "", isRefreshRef, refreshTrigger, setIsCached }) {
  const [batches, setBatches] = useState([]);
  const axiosPrivate = useAxiosPrivate();
  const listContainerRef = useRef(null);

  const [error, setError] = useState(null);

  // // Reset when search changes
  // useEffect(() => {
  //   setBatches([]);
  // }, [search]);
  const debounceSearch = useDebouncedValue(search, 500)

  const fetchBatches = useCallback(
    async (page) => {
      try {
        const shouldRefresh = isRefreshRef.current;
        const res = await axiosPrivate.get("/node/account/dashboard/allbatch", {
          params: {
            limit: LIMIT,
            // offset: page * LIMIT,
            offset: page * LIMIT,
            search: debounceSearch,
            isRefresh: shouldRefresh,
          },
        });

        // if (shouldRefresh) {
        //   isRefreshRef.current = false;
        // }
        const { data: batchData = {}, hasMore, created_at } = res.data;
        const date = new Date(created_at);
        setIsCached(date.toLocaleString());

        const incoming = Object.entries(batchData).map(([name, detailsArray]) =>
          formatBatch(name, detailsArray)
        );

        // if (page) setBatches((prev) => {
        //   const existingNames = new Set(prev.map((b) => b.name));
        //   const unique = incoming.filter((b) => !existingNames.has(b.name));
        //   return [...prev, ...unique];
        // });
        // else {
        //   console.log("2nd condition:");
        //   setBatches(incoming)
        // }

        if (page === 0) {
          setBatches(incoming);
        } else {
          setBatches((prev) => {
            const existingNames = new Set(prev.map((b) => b.name));
            const unique = incoming.filter((b) => !existingNames.has(b.name));
            return [...prev, ...unique];
          });
        }
        setError(null);
        return hasMore ? true : null;
      } catch (err) {
        console.error("Batch overview error:", err);
        setError(err);
        return null;
      }
    },
    [axiosPrivate, debounceSearch, refreshTrigger]
  );

  const { lastElementRef, loading, hasMore, setPage, setHasMore } =
    useInfiniteScroll(fetchBatches);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
  }, [debounceSearch, setPage, setHasMore]);

  // useEffect(() => {
  //   onResetRef.current = () => {
  //     setPage(0);
  //     setHasMore(true);
  //     setBatches([]);
  //     listContainerRef.current?.scrollTo({ top: 0 });
  //   };
  // }, [setPage, setHasMore]);

  // BatchOverview — replace onResetRef approach with this
  useEffect(() => {
    setBatches([]);
    setPage(0);
    setHasMore(true);
    listContainerRef.current?.scrollTo({ top: 0 });
  }, [refreshTrigger]);

  console.log("refressssss:", refreshTrigger);



  return (
    <div className="batch-overview" ref={listContainerRef}>
      <div className="batch-cards-container">
        {batches.length > 0 ? (
          batches.map((batch, index) => {
            const isLast = index === batches.length - 1;
            return (
              <BatchSection
                key={batch.name}
                data={batch}
                // Attach the sentinel ref to the last rendered card
                innerRef={isLast ? lastElementRef : null}
              />
            );
          })
        ) : !loading ? (
          <div className="no-results">No batches found</div>
        ) : null}
      </div>

      {/* Loading indicator — shown while fetching next page */}
      {loading && (
        <div className="loading-container">
          <Loading />
        </div>
      )}

      {/* End-of-list message
      {!hasMore && batches.length > 0 && (
        <div className="end-of-list">All batches loaded.</div>
      )} */}
    </div>
  );
}